/**
 * money.js — display-level currency conversion for the buyer storefront.
 *
 * The platform's canonical price unit is USD (product docs, cart math,
 * and wallet balances all store USD). This module converts that USD
 * figure into the buyer's selected shipping-country currency purely
 * for DISPLAY — no backend math changes.
 *
 * The country is chosen via the header CountryHeaderPicker, which writes
 * `buyer_country` to localStorage and reloads the page, so a plain sync
 * read here is sufficient (no context provider needed).
 *
 * FX table is static and approximate — good enough for storefront
 * display while payments are still mocked. Swap `RATES_FROM_USD` for a
 * live FX feed when real payments ship.
 */

// iso2 → ISO-4217 currency code. Missing countries fall back to USD.
const CURRENCY_BY_ISO2 = {
  US: "USD", IN: "INR", GB: "GBP",
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", BE: "EUR",
  AT: "EUR", IE: "EUR", PT: "EUR", FI: "EUR", GR: "EUR",
  JP: "JPY", AU: "AUD", CA: "CAD", SG: "SGD", AE: "AED", SA: "SAR",
  KR: "KRW", CN: "CNY", BR: "BRL", MX: "MXN", ZA: "ZAR", NG: "NGN",
  KE: "KES", EG: "EGP", PK: "PKR", BD: "BDT", LK: "LKR", NP: "NPR",
  ID: "IDR", TH: "THB", VN: "VND", PH: "PHP", MY: "MYR", TR: "TRY",
  RU: "RUB", PL: "PLN", SE: "SEK", NO: "NOK", DK: "DKK", CH: "CHF",
  NZ: "NZD", AR: "ARS", CL: "CLP", CO: "COP", PE: "PEN", IL: "ILS",
  HK: "HKD", TW: "TWD", UA: "UAH", QA: "QAR", KW: "KWD", OM: "OMR",
  BH: "BHD", JO: "JOD",
};

// Approximate static FX: how many units of currency per 1 USD.
const RATES_FROM_USD = {
  USD: 1, INR: 83, EUR: 0.92, GBP: 0.79, JPY: 150, AUD: 1.52,
  CAD: 1.36, SGD: 1.34, AED: 3.67, SAR: 3.75, KRW: 1350, CNY: 7.2,
  BRL: 5.0, MXN: 17.2, ZAR: 18.5, NGN: 1500, KES: 129, EGP: 48,
  PKR: 278, BDT: 110, LKR: 300, NPR: 133, IDR: 15600, THB: 36,
  VND: 25000, PHP: 56, MYR: 4.7, TRY: 32, RUB: 90, PLN: 4.0,
  SEK: 10.5, NOK: 10.8, DKK: 6.85, CHF: 0.88, NZD: 1.65, ARS: 950,
  CLP: 950, COP: 4200, PEN: 3.75, ILS: 3.7, HKD: 7.8, TWD: 32,
  UAH: 41, QAR: 3.64, KWD: 0.31, OMR: 0.385, BHD: 0.376, JOD: 0.709,
};

export function selectedCurrency() {
  let iso2 = "US";
  try { iso2 = (localStorage.getItem("buyer_country") || "US").toUpperCase(); } catch { /* noop */ }
  if (iso2 === "ALL") iso2 = "US";
  return CURRENCY_BY_ISO2[iso2] || "USD";
}

/** ISO-4217 code for a country iso2 (falls back to USD). */
export function currencyForIso2(iso2) {
  const key = (iso2 || "").toUpperCase();
  if (!key || key === "ALL") return "USD";
  return CURRENCY_BY_ISO2[key] || "USD";
}

/** Supported currency codes (sorted, USD first) for pickers. */
export const SUPPORTED_CURRENCIES = (() => {
  const codes = Object.keys(RATES_FROM_USD);
  return ["USD", ...codes.filter((c) => c !== "USD").sort()];
})();

/** Format a USD amount in an explicit currency code (display only). */
export function fmtCurrency(usdAmount, currency = "USD", opts = {}) {
  const rate = RATES_FROM_USD[currency] || 1;
  const value = (Number(usdAmount) || 0) * rate;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      ...(opts.decimals != null ? { maximumFractionDigits: opts.decimals } : {}),
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

/**
 * Format a USD amount in the buyer's selected currency.
 * Returns e.g. "₹11,537" when country=IN, "$139.00" when US/ALL.
 */
export function fmtMoney(usdAmount, opts = {}) {
  const currency = selectedCurrency();
  const rate = RATES_FROM_USD[currency] || 1;
  const value = (Number(usdAmount) || 0) * rate;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      ...(opts.decimals != null ? { maximumFractionDigits: opts.decimals } : {}),
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}
